import { measurementConfig, whatsAppPlacements } from "../config/measurement";
import { siteConfig } from "../config/site";
import { getTicketById, getTicketBySlug } from "../data/tickets";

export const GOOGLE_ADS_TAG_ID = measurementConfig.adsTagId;
export const GOOGLE_ANALYTICS_TAG_ID = measurementConfig.analyticsTagId;
// A new purpose (Analytics) requires a fresh choice, not a migrated Ads grant.
export const AD_CONSENT_STORAGE_KEY = "rp-measurement-consent-v2";
const LEGACY_CONSENT_STORAGE_KEY = "rp-ad-consent-v1";
const MAX_CONSENT_AGE = 180 * 24 * 60 * 60 * 1000;
const SCRIPT_ID = "rp-google-ads-tag";

export type AdConsent = "accepted" | "analytics-only" | "rejected";
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const denied = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

let initialized = false;
let adsConfigured = false;
let analyticsConfigured = false;
let tagStarted = false;
let consent: AdConsent | null = null;
let currentPath = "";
let lastPageLocation = "";
let removeClickListeners: (() => void) | undefined;
const subscribers = new Set<() => void>();

// Measurement is best-effort and must never interrupt navigation or consent controls.
function send(...args: unknown[]) {
  try { window.gtag?.(...args); } catch { /* Do not log tracking payloads. */ }
}

function readConsent(): AdConsent | null {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(AD_CONSENT_STORAGE_KEY) ?? "null");
    if (!saved || typeof saved !== "object" || !("choice" in saved) || !("savedAt" in saved)) return null;
    const age = Date.now() - Number(saved.savedAt);
    if (typeof saved.savedAt !== "number" || !Number.isFinite(age) || age < 0 || age >= MAX_CONSENT_AGE) return null;
    return saved.choice === "accepted" || saved.choice === "rejected" || saved.choice === "analytics-only" ? saved.choice : null;
  } catch {
    return null;
  }
}

export function isMeasurementOriginAllowed(origin = window.location.origin) {
  return measurementConfig.allowedOrigins.some((allowed) => allowed === origin);
}

export function getMeasurementPage(pathname: string) {
  const routes: Record<string, { type: string; title: string }> = {
    "/": { type: "home", title: "RP Abu Dhabi GP" },
    "/tickets": { type: "catalogue", title: "Tickets, yachts and Paddock" },
    "/contact": { type: "contact", title: "Contact" },
    "/terms": { type: "terms", title: "Terms and Conditions" },
    "/privacy": { type: "privacy", title: "Privacy Policy" },
    "/refund-policy": { type: "refund-policy", title: "Refund Policy" },
  };
  if (routes[pathname]) return { path: pathname, ...routes[pathname] };
  const slug = pathname.match(/^\/tickets\/([^/]+)$/)?.[1];
  const ticket = slug ? getTicketBySlug(slug) : undefined;
  return ticket
    ? { path: `/tickets/${ticket.slug}`, type: "package-detail", title: ticket.name }
    : { path: "/not-found", type: "not-found", title: "Page not found" };
}

/** Keep only known public routes and validated ad-attribution parameters. */
export function getMeasurementLocation(pathname = window.location.pathname) {
  const url = new URL(getMeasurementPage(pathname).path, window.location.origin);
  const source = new URLSearchParams(window.location.search);
  for (const key of ["gclid", "gbraid", "wbraid", "utm_source", "utm_medium", "utm_campaign", "utm_id"]) {
    const value = source.get(key);
    if (value && value.length <= 512 && /^[a-zA-Z0-9_. -]+$/.test(value)) url.searchParams.set(key, value);
  }
  return url.toString();
}

function safeReferrer() {
  try {
    const referrer = new URL(document.referrer);
    return referrer.origin === window.location.origin
      ? new URL(getMeasurementPage(referrer.pathname).path, referrer.origin).toString()
      : referrer.origin;
  } catch {
    return "";
  }
}

function enableMeasurement() {
  if (!isMeasurementOriginAllowed()) return;
  const advertising = consent === "accepted";
  send("consent", "update", {
    ...denied,
    analytics_storage: "granted",
    ad_storage: advertising ? "granted" : "denied",
    ad_user_data: advertising ? "granted" : "denied",
  });
  send("set", {
    page_location: getMeasurementLocation(currentPath || window.location.pathname),
    page_referrer: safeReferrer(),
  });
  if (!tagStarted) {
    tagStarted = true;
    send("js", new Date());
  }
  if (advertising && !adsConfigured) {
    adsConfigured = true;
    send("config", GOOGLE_ADS_TAG_ID, { allow_ad_personalization_signals: false });
  }
  if (!analyticsConfigured) {
    analyticsConfigured = true;
    send("config", GOOGLE_ANALYTICS_TAG_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  }
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${advertising ? GOOGLE_ADS_TAG_ID : GOOGLE_ANALYTICS_TAG_ID}`;
    document.head.append(script);
  }
  trackMeasurementPage(currentPath || window.location.pathname);
}

export function initializeGoogleAds() {
  if (initialized) return;
  initialized = true;
  window.dataLayer = window.dataLayer || [];
  // Google's queue contract uses an Arguments object, not an ordinary event array.
  // eslint-disable-next-line prefer-rest-params -- Required by the documented gtag bootstrap contract.
  window.gtag = window.gtag || function () { window.dataLayer?.push(arguments); };
  send("consent", "default", denied);
  send("set", "ads_data_redaction", true);
  try { localStorage.removeItem(LEGACY_CONSENT_STORAGE_KEY); } catch { /* A blocked legacy key is never read. */ }
  consent = readConsent();
  if (consent === "accepted" || consent === "analytics-only") enableMeasurement();
  subscribers.forEach((subscriber) => subscriber());
}

export function getAdConsent() { return consent; }

export function subscribeAdConsent(subscriber: () => void) {
  subscribers.add(subscriber);
  return () => { subscribers.delete(subscriber); };
}

/** Returns true when a reload is needed to unload a previously granted Google tag. */
export function setAdConsent(choice: AdConsent) {
  initializeGoogleAds();
  let shouldReload = (adsConfigured && choice !== "accepted") || (analyticsConfigured && choice === "rejected");
  consent = choice;
  try {
    localStorage.setItem(AD_CONSENT_STORAGE_KEY, JSON.stringify({ choice, savedAt: Date.now() }));
  } catch {
    // This page still respects the choice when browser storage is unavailable.
    if (choice !== "accepted") {
      try {
        // Remove any stale grant so a reload cannot accidentally restore it.
        localStorage.removeItem(AD_CONSENT_STORAGE_KEY);
      } catch {
        shouldReload = false;
      }
    }
  }
  if (choice === "accepted" || choice === "analytics-only") enableMeasurement();
  else if (adsConfigured || analyticsConfigured) send("consent", "update", denied);
  subscribers.forEach((subscriber) => subscriber());
  return shouldReload;
}

export function openCookieSettings() {
  window.dispatchEvent(new Event("rp-open-cookie-settings"));
}

/** Manual SPA page views: no automatic history, URL-query or outbound-link collection. */
export function trackMeasurementPage(pathname: string) {
  currentPath = pathname;
  if (!initialized) initializeGoogleAds();
  if (!analyticsConfigured || consent === null || consent === "rejected" || !isMeasurementOriginAllowed()) return;
  const location = getMeasurementLocation(pathname);
  if (location === lastPageLocation) return;
  const page = getMeasurementPage(pathname);
  send("set", { page_location: location, page_referrer: lastPageLocation || safeReferrer() });
  send("event", "page_view", {
    send_to: GOOGLE_ANALYTICS_TAG_ID,
    page_location: location,
    page_referrer: lastPageLocation || safeReferrer(),
    page_title: page.title,
    page_type: page.type,
  });
  lastPageLocation = location;
}

/** Click intent only: does not mean a message was sent, a lead qualified or a sale occurred. */
export function trackWhatsAppClick(anchor: HTMLAnchorElement) {
  if (!initialized || consent === null || consent === "rejected" || !isMeasurementOriginAllowed()) return false;
  const placement = anchor.dataset.whatsappLocation;
  if (!whatsAppPlacements.some((value) => value === placement)) return false;
  let destination: URL;
  try { destination = new URL(anchor.href); } catch { return false; }
  const business = new URL(siteConfig.whatsappUrl);
  if (destination.origin !== business.origin || destination.pathname !== business.pathname || destination.username || destination.password) return false;

  const page = getMeasurementPage(currentPath || window.location.pathname);
  const location = getMeasurementLocation(currentPath || window.location.pathname);
  const ticket = anchor.dataset.whatsappPackageId ? getTicketById(anchor.dataset.whatsappPackageId) : undefined;
  if (analyticsConfigured) {
    send("event", "whatsapp_click", {
      send_to: GOOGLE_ANALYTICS_TAG_ID,
      page_location: location,
      page_type: page.type,
      link_location: placement,
      ...(ticket ? { package_id: ticket.id, package_category: ticket.category } : {}),
    });
  }
  if (adsConfigured && consent === "accepted") {
    send("event", "conversion", {
      send_to: measurementConfig.whatsappConversionTarget,
      page_location: location,
    });
  }
  return true;
}

/** One delegated handler covers nested icons, keyboard activation and lazy route content. */
export function initializeWhatsAppClickTracking() {
  if (removeClickListeners) return removeClickListeners;
  const click = (event: MouseEvent) => {
    if (event.defaultPrevented || (event.type === "click" ? event.button !== 0 : event.button !== 1)) return;
    const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[data-whatsapp-location]") : null;
    if (anchor) trackWhatsAppClick(anchor);
    // Never preventDefault or wait for Google: WhatsApp must still open when tracking is blocked.
  };
  document.addEventListener("click", click);
  document.addEventListener("auxclick", click);
  removeClickListeners = () => {
    document.removeEventListener("click", click);
    document.removeEventListener("auxclick", click);
    removeClickListeners = undefined;
  };
  return removeClickListeners;
}
